/** Conversation manager in the chat head row (Build and Test): pick an earlier
 *  conversation, start a new one or delete one (after the platform's confirmation
 *  modal; the conversation the builder is working in cannot be deleted). With
 *  onDeleteMany every row has a checkbox: tick some or all, then delete them
 *  together (one confirmation); a click on the title still opens the conversation. */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faComments, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../confirmationModal';
import { guardWrite } from '../../utils/permissions';
import { timeAgo } from './studioApi';
import { Badge } from './ui';

function SelectAll({ checked, partly, onChange, label, disabled }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = partly; }, [partly]);
  return (
    <label className={`as-menu-all ${disabled ? 'is-disabled' : ''}`}>
      <input ref={ref} type="checkbox" className="as-menu-select" checked={checked} onChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}

export default function ConversationPicker({
  conversations, currentId, onOpen, onNew, onDelete, onDeleteMany, newDisabled, menuTitle, runningId,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null); // one conversation, or { ids } for the ticked ones
  const [picked, setPicked] = useState(() => new Set());
  const title = (c) => c?.title || t('agent_studio_conversation_untitled');
  const current = conversations.find((c) => c.id === currentId);
  const locked = (c) => !!runningId && c.id === runningId; // Mo is working in it
  const checklist = !!onDeleteMany;
  const selectable = conversations.filter((c) => !locked(c));
  const chosen = selectable.filter((c) => picked.has(c.id)).map((c) => c.id);

  const close = () => { setOpen(false); setPicked(new Set()); };
  const toggle = (id) => setPicked((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setPicked(chosen.length === selectable.length ? new Set() : new Set(selectable.map((c) => c.id)));
  const askDeleteMany = () => {
    if (!chosen.length || !guardWrite()) return;
    setToDelete({ ids: chosen });
    close();
  };
  const confirmDelete = () => (toDelete?.ids ? onDeleteMany(toDelete.ids) : onDelete(toDelete.id));

  return (
    <>
      <div className={`as-menu-wrap ${open ? 'is-open' : ''}`}>
        <button type="button" className="as-convo-btn" onClick={() => (open ? close() : setOpen(true))} aria-expanded={open}
          data-pill-anchor>
          <FontAwesomeIcon icon={faComments} className="as-convo-ic" />
          <span className="as-convo-title">{current ? title(current) : t('agent_studio_test_new_conversation')}</span>
          <FontAwesomeIcon icon={faChevronDown} className="as-convo-arrow" />
        </button>
        {open && (
          <>
            <div className="as-menu-backdrop" onClick={close} role="presentation" />
            <div className={`as-menu ${checklist ? 'is-checklist' : ''}`}>
              <div className="as-menu-head">
                <span>{menuTitle}</span>
                {checklist && conversations.length > 0 && (
                  <span className={`as-menu-count ${chosen.length ? 'is-active' : ''}`} aria-live="polite">
                    {t('agent_studio_conv_selected', { count: chosen.length })}
                  </span>
                )}
              </div>
              {conversations.length === 0 && <div className="as-small as-muted as-pad-sm">{t('agent_studio_test_no_conversations')}</div>}
              {conversations.map((c) => {
                const on = picked.has(c.id) && !locked(c);
                return (
                  <div key={c.id} className={`as-menu-row ${c.id === currentId ? 'is-current' : ''} ${on ? 'is-selected' : ''}`}>
                    {checklist ? (
                      <input type="checkbox" className="as-menu-select" checked={on} disabled={locked(c)}
                        onChange={() => toggle(c.id)} aria-label={title(c)} />
                    ) : (
                      <span className="as-menu-check">{c.id === currentId && <FontAwesomeIcon icon={faCheck} />}</span>
                    )}
                    <button type="button" className="as-menu-item" onClick={() => { close(); onOpen(c.id); }}>
                      <span className="as-ellipsis" style={{ maxWidth: '100%' }}>{title(c)}</span>
                      <span className="as-small">
                        {timeAgo(c.updated_at, t, i18n.language)}
                        {locked(c) && <> · <Badge tone="brand">{t('agent_studio_running_badge')}</Badge></>}
                      </span>
                    </button>
                    {onDelete && !locked(c) && (
                      <button type="button" className="as-icon-btn" title={t('agent_studio_delete')}
                        onClick={() => { if (!guardWrite()) return; close(); setToDelete(c); }}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                );
              })}
              {checklist && conversations.length > 0 && (
                <div className="as-menu-foot">
                  <SelectAll checked={chosen.length > 0 && chosen.length === selectable.length}
                    partly={chosen.length > 0 && chosen.length < selectable.length} disabled={!selectable.length}
                    onChange={toggleAll} label={t('agent_studio_conv_select_all')} />
                  <button type="button" className="as-btn-danger-sm" disabled={!chosen.length} onClick={askDeleteMany}>
                    <FontAwesomeIcon icon={faTrash} /> {t('agent_studio_conv_delete_selected', { count: chosen.length })}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <button type="button" className="as-convo-new" onClick={() => { close(); onNew(); }} disabled={newDisabled}
        title={t('agent_studio_test_new_conversation')}>
        <FontAwesomeIcon icon={faPlus} /> <span className="as-convo-new-label">{t('agent_studio_test_new_conversation')}</span>
      </button>
      {(onDelete || onDeleteMany) && (
        <ConfirmationModal
          open={!!toDelete}
          onClose={() => setToDelete(null)}
          onConfirm={confirmDelete}
          itemName={!toDelete ? '' : toDelete.ids
            ? t(toDelete.ids.length === 1 ? 'agent_studio_conversations_item_one' : 'agent_studio_conversations_item', { count: toDelete.ids.length })
            : t('agent_studio_conversation_item', { title: title(toDelete) })}
        />
      )}
    </>
  );
}
